import React, { useMemo, useState, useEffect } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import {
  Building2,
  LayoutGrid,
  UserRound,
} from "lucide-react";

// eslint-disable-next-line no-unused-vars
import company1 from "../../assets/img/company1.svg";
// eslint-disable-next-line no-unused-vars
import company2 from "../../assets/img/company2.svg";
import PeopleViewCard from "../../components/dashboard/PeopleViewCard";
import SurveyViewCard from "../../components/dashboard/SurveyViewCard";
import CompaniesViewCard from "../../components/dashboard/CompaniesViewCard";
import Pagination from "../../components/dashboard/Pagination";
import { globalSearch } from "../../service/globalSearch";


const searchTabs = [
  {
    id: "all",
    label: "All",
    count: 530,
    icon: LayoutGrid,
  },
  {
    id: "survey",
    label: "Survey",
    count: 29,
    icon: LayoutGrid,
  },
  {
    id: "people",
    label: "People",
    count: 76,
    icon: UserRound,
  },
  {
    id: "companies",
    label: "Companies",
    count: 51,
    icon: Building2,
  },
];

const tabContent = {
  all: [
    {
      id: 1,
      type: "survey",
      title: "Remote Work Collaboration Habits - Q1 2025 Edition",
      description:
        "A Five-Minute Benchmark Survey For Product, Operations, And People Leaders Working With Distributed Teams.",
      tags: ["#Technology", "#SaaS", "#Workplace"],
      reward: 25,
      cta: "Start Survey",
      start_date: "2026-01-10",
    },
    {
      id: 2,
      type: "survey",
      title: "AI Adoption Trends In Product Teams",
      description:
        "Understand How Product Managers, Analysts, And Research Teams Are Integrating AI Into Their Weekly Workflows.",
      tags: ["#AI", "#Product", "#Research"],
      reward: 20,
      cta: "Start Survey",
      start_date: "2026-01-10",
    },
    {
      id: 3,
      name: "Aisha Mehta",
      role: "Product Researcher",
      company: "Northlane Labs",
      location: "Bengaluru, India",
      stats: "18 relevant surveys matched",
      skills: ["UX Research", "B2B SaaS", "AI Workflows"],
    },
  ],
  survey: [],
  people: [],
  companies: [],
};


const SearchResults = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [searchParams, setSearchParams] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false);

  // Tab-specific totals and pages
  const [companiesTotal, setCompaniesTotal] = useState(51);
  const [companiesPages, setCompaniesPages] = useState(1);
  const [usersTotal, setUsersTotal] = useState(76);
  const [usersPages, setUsersPages] = useState(1);
  const [surveysTotal, setSurveysTotal] = useState(29);
  const [surveysPages, setSurveysPages] = useState(1);

  const searchTerm = searchParams.get("searchTerm") || "";
  const currentPage = parseInt(searchParams.get("page") || "1", 10);

  const activeTabConfig = useMemo(
    () => searchTabs.find((tab) => tab.id === activeTab) ?? searchTabs[0],
    [activeTab]
  );

  const activeResults = useMemo(() => {
    const hasToken = !!localStorage.getItem("token");
    if (activeTab === "companies") {
      return hasToken ? companies : (companies.length > 0 ? companies : (tabContent.companies || []));
    }
    if (activeTab === "people") {
      return hasToken ? users : (users.length > 0 ? users : (tabContent.people || []));
    }
    if (activeTab === "survey") {
      return hasToken ? surveys : (surveys.length > 0 ? surveys : (tabContent.survey || []));
    }
    return tabContent[activeTab] ?? [];
  }, [activeTab, companies, users, surveys]);

  const { setSearchActiveTab } = useOutletContext();

  useEffect(() => {
    setSearchActiveTab(activeTab);
  }, [activeTab]);

  // Fetch search results via globalSearch API
  useEffect(() => {
    const fetchSearchResults = async () => {
      try {
        setLoading(true);
        const payload = {
          searchTerm: searchTerm,
          page: currentPage,
          limit: 15,
          // Survey parameters
          survey_sector: searchParams.get("survey_sector") || "",
          survey_industry: searchParams.get("survey_industry") || "",
          survey_country: searchParams.get("survey_country") || "",
          date_posted: (searchParams.get("date_posted") || "").toLowerCase() === "custom" ? "" : (searchParams.get("date_posted") || ""),
          start_date: searchParams.get("start_date") || "",
          end_date: searchParams.get("end_date") || "",
          // People parameters
          people_industry: searchParams.get("people_industry") || "",
          people_job_title: searchParams.get("people_job_title") || "",
          people_company: searchParams.get("people_company") || "",
          people_country: searchParams.get("people_country") || "",
          // Company parameters
          company_industry: searchParams.get("company_industry") || "",
          company_location: searchParams.get("company_location") || "",
          company_size: searchParams.get("company_size") || "",
          company_type: searchParams.get("company_type") || ""
        };

        console.log("[SearchResults] Calling globalSearch with payload:", payload);
        const response = await globalSearch(payload);
        console.log("[SearchResults] globalSearch response:", response);

        if (response.status === 200 && response.data?.success) {
          const comps = response.data.companies?.data || [];
          setCompanies(comps);
          setCompaniesTotal(response.data.companies?.pagination?.total ?? 0);
          setCompaniesPages(response.data.companies?.pagination?.totalPages ?? 1);

          const usrs = response.data.users?.data || [];
          setUsers(usrs);
          setUsersTotal(response.data.users?.pagination?.total ?? 0);
          setUsersPages(response.data.users?.pagination?.totalPages ?? 1);

          const svys = response.data.surveys?.data || [];
          setSurveys(svys);
          setSurveysTotal(response.data.surveys?.pagination?.total ?? 0);
          setSurveysPages(response.data.surveys?.pagination?.totalPages ?? 1);
        }
      } catch (error) {
        console.error("[SearchResults] Error fetching global search:", error);
        const hasToken = !!localStorage.getItem("token");

        setCompanies([]);
        setCompaniesTotal(hasToken ? 0 : (tabContent.companies ? tabContent.companies.length : 2));
        setCompaniesPages(1);

        setUsers([]);
        setUsersTotal(hasToken ? 0 : (tabContent.people ? tabContent.people.length : 3));
        setUsersPages(1);

        setSurveys([]);
        setSurveysTotal(hasToken ? 0 : (tabContent.survey ? tabContent.survey.length : 3));
        setSurveysPages(1);
      } finally {
        setLoading(false);
      }
    };

    fetchSearchResults();
  }, [searchParams, activeTab]);

  const getTabCount = (tabId) => {
    if (tabId === "companies") {
      return companiesTotal;
    }
    if (tabId === "people") {
      return usersTotal;
    }
    if (tabId === "survey") {
      return surveysTotal;
    }
    const found = searchTabs.find((t) => t.id === tabId);
    return found ? found.count : 0;
  };

  const getActiveTabTotalCount = () => {
    if (activeTab === "companies") return companiesTotal;
    if (activeTab === "people") return usersTotal;
    if (activeTab === "survey") return surveysTotal;
    return activeTabConfig.count;
  };

  return (
    <div className="dashboard-search-results">

      <div className="row g-3">
        <div className="col-12">
          <div className="sr-shell-card p-3 p-md-4">
            <div className="row g-3">
              <div className="col-12">
                <h1 className="sr-page-title">
                  Search Results For <span>"{searchTerm ? searchTerm : "Remote Collaboration Survey"}"</span>
                </h1>
                <p className="sr-page-subtitle">
                  {getActiveTabTotalCount()} Results Found
                </p>
              </div>

              <div className="col-12">
                <div className="sr-tabs-shell">
                  <div className="row g-2">
                    {searchTabs.map((tab) => {
                      const Icon = tab.icon;
                      return (
                        <div className="col-12 col-sm-3" key={tab.id}>
                          <button
                            type="button"
                            className={`sr-tab-btn ${activeTab === tab.id ? "active" : ""}`}
                            onClick={() => {
                              setActiveTab(tab.id);
                              setSearchActiveTab(tab.id);
                              // Reset page param to 1 when changing tabs
                              const params = new URLSearchParams(window.location.search);
                              params.set("page", "1");
                              setSearchParams(params);
                            }}
                          >
                            <Icon size={14} />
                            <span>{tab.label}</span>
                            <span className="sr-tab-count">({getTabCount(tab.id)})</span>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {loading && (activeTab === "companies" || activeTab === "survey" || activeTab === "people") ? (
          <div className="col-12 text-center py-5">
            <div className="spinner-border text-danger" role="status" style={{ color: "#c72430" }}>
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (activeTab === "companies" || activeTab === "survey" || activeTab === "people") && activeResults.length === 0 ? (
          <div className="col-12 text-center py-5 bg-white border border-light rounded-4 shadow-sm my-4" style={{ borderRadius: "16px" }}>
            <div className="d-inline-flex align-items-center justify-content-center bg-light rounded-circle mb-3" style={{ width: "80px", height: "80px" }}>
              <span style={{ fontSize: "36px" }}>🔍</span>
            </div>
            <h3 className="fw-bold text-dark mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>No {activeTabConfig.label} Found</h3>
            <p className="text-muted mx-auto" style={{ maxWidth: "400px", fontSize: "14px" }}>
              We couldn't find any {activeTabConfig.label.toLowerCase()} matching your search criteria. Try adjusting your sidebar filters or modifying your search term.
            </p>
          </div>
        ) : (
          activeResults.map((item) => (
            <div className="col-12" key={`${activeTab}-${item.id || item.company_id || item.member_id}`}>
              {activeTab === "all" && (
                <SurveyViewCard item={item} />
              )}

              {activeTab === "survey" && (
                <SurveyViewCard item={item} />
              )}

              {activeTab === "people" && (
                <PeopleViewCard item={item} />
              )}

              {activeTab === "companies" && (
                <CompaniesViewCard item={item} />
              )}
            </div>
          ))
        )}

        {/* Pagination */}
        {(activeTab === "companies" || activeTab === "survey" || activeTab === "people") && !loading && activeResults.length > 0 && (
          <div className="col-12 mt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={
                activeTab === "companies"
                  ? companiesPages
                  : activeTab === "survey"
                    ? surveysPages
                    : usersPages
              }
              onPageChange={(pageNumber) => {
                const params = new URLSearchParams(window.location.search);
                params.set("page", String(pageNumber));
                setSearchParams(params);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResults;
